// client/src/pages/sections/DisengagementFields.js
// Reusable fieldset for the client-disengagement follow-up form. Renders the
// disengagement-form questions AR needs when a client is marked NLC/Culled/
// Stale (see the "Process for NLC/Inactive Client" thread). Controlled: parent
// owns the value object + onChange; validation lives in disengagementOptions.js.
//
// Used by DisengagementModal (Stale Clients + Client Contact) and embedded in
// the Client Groups MarkNlcButton alongside its SG/RG resolution.
//
// `clientCode` (optional) drives the "related parties" picker: other clients in
// the same signer/relationship group, fetched from /api/dgRelatedParties, that
// the user can also sweep into the disengagement (chips), plus a search box
// (/api/dgClientLookup) to add codes not listed.

import { useEffect, useState } from 'react';
import {
  DISENGAGEMENT_TYPE_OPTIONS,
  REASONS_BY_TYPE,
  PROGRAM_OPTIONS,
  PROGRAM_NONE,
  LETTER_DELIVERY_OPTIONS,
  DISENGAGEMENT_REQUIRED,
  REASONS_NEEDING_NOTES,
} from './disengagementOptions';

const req = DISENGAGEMENT_REQUIRED;
const programLabel = (code) => (PROGRAM_OPTIONS.find(o => o.value === code) || {}).label || code;

export default function DisengagementFields({ value, onChange, disabled, errors = {}, clientCode }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const setType = (disengagementType) => {
    const stillValid = (REASONS_BY_TYPE[disengagementType] || []).includes(v.reasonCategory);
    onChange({ ...v, disengagementType, reasonCategory: stillValid ? v.reasonCategory : null });
  };

  const reasons = REASONS_BY_TYPE[v.disengagementType] || [];
  const notesRequired = req.notes || REASONS_NEEDING_NOTES.includes(v.reasonCategory);

  // ── programs (multi-select) ──────────────────────────────────────────────
  const programs = Array.isArray(v.programsToSeparate) ? v.programsToSeparate : [];
  const addProgram = (code) => {
    if (!code) return;
    // 'None' is exclusive; adding any real program clears 'None' and vice versa.
    const next = code === PROGRAM_NONE
      ? [PROGRAM_NONE]
      : [...programs.filter(p => p !== PROGRAM_NONE), code];
    set({ programsToSeparate: [...new Set(next)] });
  };
  const removeProgram = (code) => set({ programsToSeparate: programs.filter(p => p !== code) });
  const programsToAdd = PROGRAM_OPTIONS.filter(o => !programs.includes(o.value));

  // ── related parties (chips + search) ─────────────────────────────────────
  const selected = Array.isArray(v.relatedParties) ? v.relatedParties : [];
  const isSelected = (code) => selected.some(p => p.clientCode === code);
  const toggleParty = (party) => {
    if (isSelected(party.clientCode)) {
      set({ relatedParties: selected.filter(p => p.clientCode !== party.clientCode) });
    } else {
      set({ relatedParties: [...selected, { clientCode: party.clientCode, clientName: party.clientName || null, contIndex: party.contIndex || null }] });
    }
  };

  const [pool, setPool]         = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);

  // Fetch same-group related parties when a clientCode is provided.
  useEffect(() => {
    if (!clientCode) { setPool([]); return undefined; }
    let cancelled = false;
    setPoolLoading(true);
    fetch(`/api/dgRelatedParties/${encodeURIComponent(clientCode)}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { parties: [] }))
      .then(d => { if (!cancelled) setPool(d.parties || []); })
      .catch(() => { if (!cancelled) setPool([]); })
      .finally(() => { if (!cancelled) setPoolLoading(false); });
    return () => { cancelled = true; };
  }, [clientCode]);

  // Debounced client search for the manual-add box.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`/api/dgClientLookup?q=${encodeURIComponent(q)}`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : { results: [] }))
        .then(d => { if (!cancelled) setResults(d.results || []); })
        .catch(() => { if (!cancelled) setResults([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const poolCodes = new Set(pool.map(p => p.clientCode));
  // Manually-added selected parties that aren't in the fetched pool — render as
  // their own removable chips so they don't disappear.
  const extraSelected = selected.filter(p => !poolCodes.has(p.clientCode));
  // Search results excluding self, already-in-pool, already-selected.
  const searchHits = results.filter(r =>
    r.clientCode !== clientCode && !poolCodes.has(r.clientCode) && !isSelected(r.clientCode));

  return (
    <div className="dis-fields">
      {/* Disengagement type */}
      <fieldset className="dis-field">
        <legend className="dis-field-label">
          Did we terminate our services, or did the client?
          {req.disengagementType && <span className="dis-req" aria-hidden="true">*</span>}
        </legend>
        {DISENGAGEMENT_TYPE_OPTIONS.map((opt) => (
          <label className="dis-radio-row" key={opt.value}>
            <input
              type="radio"
              name="dis-type"
              value={opt.value}
              checked={v.disengagementType === opt.value}
              disabled={disabled}
              onChange={() => setType(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
        {errors.disengagementType && <div className="dis-field-error">{errors.disengagementType}</div>}
      </fieldset>

      {/* Conditional reason */}
      {v.disengagementType && (
        <fieldset className="dis-field">
          <legend className="dis-field-label">
            {v.disengagementType === 'bmss_terminated'
              ? 'Reason BMSS terminated the client'
              : 'Reason the client terminated our services'}
            {req.reasonCategory && <span className="dis-req" aria-hidden="true">*</span>}
          </legend>
          {reasons.map((r) => (
            <label className="dis-radio-row" key={r}>
              <input
                type="radio"
                name="dis-reason"
                value={r}
                checked={v.reasonCategory === r}
                disabled={disabled}
                onChange={() => set({ reasonCategory: r })}
              />
              <span>{r}</span>
            </label>
          ))}
          {errors.reasonCategory && <div className="dis-field-error">{errors.reasonCategory}</div>}
        </fieldset>
      )}

      {/* Which firm did we lose them to */}
      <div className="dis-field">
        <label className="dis-field-label" htmlFor="dis-lostToFirm">
          Which accounting firm did we lose this client to?
          {req.lostToFirm && <span className="dis-req" aria-hidden="true">*</span>}
        </label>
        <input
          id="dis-lostToFirm"
          type="text"
          className="dis-input"
          placeholder="If you know where they're moving, note it here (optional)"
          value={v.lostToFirm || ''}
          disabled={disabled}
          maxLength={200}
          onChange={(e) => set({ lostToFirm: e.target.value })}
        />
        {errors.lostToFirm && <div className="dis-field-error">{errors.lostToFirm}</div>}
      </div>

      {/* Programs to separate (multi-select) */}
      <div className="dis-field">
        <label className="dis-field-label" htmlFor="dis-programs">
          Which programs need to be separated from the firm?
          {req.programsToSeparate && <span className="dis-req" aria-hidden="true">*</span>}
        </label>
        <select
          id="dis-programs"
          className="dis-select"
          value=""
          disabled={disabled || programsToAdd.length === 0}
          onChange={(e) => { addProgram(e.target.value); e.target.value = ''; }}
        >
          <option value="">{programs.length ? 'Add another program…' : 'Select one or more…'}</option>
          {programsToAdd.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {programs.length > 0 && (
          <div className="dis-chipbox">
            {programs.map((code) => (
              <span className="dis-chip" key={code}>
                {programLabel(code)}
                <button type="button" className="dis-chip-x" disabled={disabled}
                        onClick={() => removeProgram(code)} aria-label={`Remove ${programLabel(code)}`}>×</button>
              </span>
            ))}
          </div>
        )}
        {errors.programsToSeparate && <div className="dis-field-error">{errors.programsToSeparate}</div>}
      </div>

      {/* Letter needed + delivery */}
      <fieldset className="dis-field">
        <legend className="dis-field-label">
          Will we need to send this client a disengagement letter?
          {req.disengagementLetterNeeded && <span className="dis-req" aria-hidden="true">*</span>}
        </legend>
        <div className="dis-inline-radios">
          <label className="dis-radio-row">
            <input
              type="radio"
              name="dis-letter"
              checked={v.disengagementLetterNeeded === true}
              disabled={disabled}
              onChange={() => set({ disengagementLetterNeeded: true })}
            />
            <span>Yes</span>
          </label>
          <label className="dis-radio-row">
            <input
              type="radio"
              name="dis-letter"
              checked={v.disengagementLetterNeeded === false}
              disabled={disabled}
              onChange={() => set({ disengagementLetterNeeded: false, letterDeliveryMethod: null })}
            />
            <span>No</span>
          </label>
        </div>
        {errors.disengagementLetterNeeded && <div className="dis-field-error">{errors.disengagementLetterNeeded}</div>}

        {v.disengagementLetterNeeded === true && (
          <div className="dis-conditional">
            <span className="dis-field-label">
              Delivery method
              {req.letterDeliveryMethod && <span className="dis-req" aria-hidden="true">*</span>}
            </span>
            <div className="dis-inline-radios">
              {LETTER_DELIVERY_OPTIONS.map((opt) => (
                <label className="dis-radio-row" key={opt.value}>
                  <input
                    type="radio"
                    name="dis-delivery"
                    value={opt.value}
                    checked={v.letterDeliveryMethod === opt.value}
                    disabled={disabled}
                    onChange={() => set({ letterDeliveryMethod: opt.value })}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            {errors.letterDeliveryMethod && <div className="dis-field-error">{errors.letterDeliveryMethod}</div>}
          </div>
        )}
      </fieldset>

      {/* Related parties — also disengage clients in the same signer/relationship group */}
      {clientCode && (
        <div className="dis-field">
          <label className="dis-field-label">Also disengage related parties?</label>
          <p className="dis-help">
            Other clients in this client's signer or relationship group. Select any that
            should be marked No Longer Client with the same answers above.
          </p>

          {poolLoading && <p className="dis-help">Checking for related parties…</p>}
          {!poolLoading && pool.length === 0 && extraSelected.length === 0 && (
            <p className="dis-help">No related parties found in the same group.</p>
          )}

          {(pool.length > 0 || extraSelected.length > 0) && (
            <div className="dis-chipbox dis-chipbox--parties">
              {pool.map((p) => (
                <button
                  type="button"
                  key={p.clientCode}
                  className={`dis-party-chip${isSelected(p.clientCode) ? ' is-selected' : ''}`}
                  disabled={disabled}
                  onClick={() => toggleParty(p)}
                  title={`${p.clientCode} — ${p.clientName || ''} (${p.via === 'rg' ? 'relationship group' : 'signer group'})`}
                >
                  <span className="dis-party-check">{isSelected(p.clientCode) ? '✓' : '+'}</span>
                  <code>{p.clientCode}</code> {p.clientName || ''}
                </button>
              ))}
              {extraSelected.map((p) => (
                <span className="dis-party-chip is-selected is-manual" key={p.clientCode}>
                  <code>{p.clientCode}</code> {p.clientName || ''}
                  <button type="button" className="dis-chip-x" disabled={disabled}
                          onClick={() => toggleParty(p)} aria-label={`Remove ${p.clientCode}`}>×</button>
                </span>
              ))}
            </div>
          )}

          {/* Manual add by code / name */}
          <div className="dis-party-search">
            <input
              type="text"
              className="dis-input"
              placeholder="Add another client by code or name…"
              value={search}
              disabled={disabled}
              onChange={(e) => setSearch(e.target.value)}
            />
            {searchHits.length > 0 && (
              <ul className="dis-search-results">
                {searchHits.map((r) => (
                  <li key={r.clientCode}>
                    <button type="button" disabled={disabled}
                            onClick={() => { toggleParty(r); setSearch(''); setResults([]); }}>
                      <code>{r.clientCode}</code> {r.clientName || ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {selected.length > 0 && (
            <p className="dis-help dis-help--strong">
              {selected.length} related part{selected.length === 1 ? 'y' : 'ies'} will also be marked NLC.
            </p>
          )}
        </div>
      )}

      {/* Notes */}
      <div className="dis-field">
        <label className="dis-field-label" htmlFor="dis-notes">
          Notes
          {notesRequired && <span className="dis-req" aria-hidden="true">*</span>}
        </label>
        <textarea
          id="dis-notes"
          className="dis-textarea"
          placeholder={REASONS_NEEDING_NOTES.includes(v.reasonCategory)
            ? `Explain the "${v.reasonCategory}" reason`
            : 'Anything else AR should know (optional)'}
          value={v.notes || ''}
          disabled={disabled}
          onChange={(e) => set({ notes: e.target.value })}
        />
        {errors.notes && <div className="dis-field-error">{errors.notes}</div>}
      </div>
    </div>
  );
}
